import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// We'll use new API calls (region-based)
import {
    getRegionsList,
    getRegionDetails,
    createRegion,
    updateRegion,
    deleteRegion
} from './api';

const MarketDetailsPage = () => {
    const { packageName, marketName } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('token');

    const [regionNames, setRegionNames] = useState([]);  // array of region names, e.g. ["default", "IR", ...]
    const [selectedRegion, setSelectedRegion] = useState(''); // which region tab is selected
    const [regionData, setRegionData] = useState(null);  // data for the selected region
    const [newRegionName, setNewRegionName] = useState(''); // for creating a new region

    // 1. Load all region names
    const loadRegions = async () => {
        try {
            const { regions } = await getRegionsList(token, packageName, marketName);
            setRegionNames(regions);

            // if we don't have a selected region or it's missing, pick the first one
            if (regions.length > 0 && !regions.includes(selectedRegion)) {
                setSelectedRegion(regions[0]);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // 2. Load data for a selected region
    const loadRegionData = async (regionName) => {
        try {
            const { regionData } = await getRegionDetails(token, packageName, marketName, regionName);
            setRegionData(regionData);
        } catch (err) {
            console.error(err);
            setRegionData(null);
        }
    };

    useEffect(() => {
        loadRegions();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (selectedRegion) {
            loadRegionData(selectedRegion);
        }
        // eslint-disable-next-line
    }, [selectedRegion]);

    // Handle region creation
    const handleCreateRegion = async () => {
        if (!newRegionName) return;
        try {
            await createRegion(token, packageName, marketName, newRegionName);
            setNewRegionName('');
            await loadRegions();
            alert('Region created successfully.');
        } catch (err) {
            console.error(err);
            alert('Failed to create region.');
        }
    };

    // Handle region deletion
    const handleDeleteRegion = async (region) => {
        if (!window.confirm(`Are you sure you want to delete region "${region}"?`)) return;
        try {
            await deleteRegion(token, packageName, marketName, region);
            // reload region list
            await loadRegions();
            // if we deleted the currently selected region, reset
            if (region === selectedRegion) {
                setSelectedRegion('');
                setRegionData(null);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Handle region data save
    const handleSaveRegion = async () => {
        if (!selectedRegion || !regionData) return;
        try {
            await updateRegion(token, packageName, marketName, selectedRegion, regionData);
            alert('Region updated successfully.');
        } catch (err) {
            console.error(err);
            alert('Failed to update region.');
        }
    };

    // Add server to regionData
    const handleAddServer = () => {
        if (!regionData) return;
        const newServer = { name: '', host: '', scheme: '', port: '' };
        setRegionData({
            ...regionData,
            servers: [...(regionData.servers || []), newServer]
        });
    };

    // Remove server from regionData
    const handleRemoveServer = (index) => {
        if (!regionData || !regionData.servers) return;
        const updatedServers = [...regionData.servers];
        updatedServers.splice(index, 1);
        setRegionData({ ...regionData, servers: updatedServers });
    };

    return (
        <div className="container mt-4">
            <h2>Market Details for <strong>{marketName}</strong> in <strong>{packageName}</strong></h2>

            {/* Region Tabs */}
            <ul className="nav nav-tabs mb-3">
                {regionNames.map((region) => (
                    <li className="nav-item" key={region}>
                        <button
                            className={`nav-link ${region === selectedRegion ? 'active' : ''}`}
                            onClick={() => setSelectedRegion(region)}
                        >
                            {region}
                        </button>
                        {/* Tiny delete button next to region tab */}
                        <button
                            className="btn btn-sm btn-danger ms-2"
                            onClick={() => handleDeleteRegion(region)}
                        >
                            x
                        </button>
                    </li>
                ))}
            </ul>

            {/* Add a new region */}
            <div className="mb-3 d-flex" style={{ maxWidth: 300 }}>
                <input
                    type="text"
                    className="form-control"
                    placeholder="New region name"
                    value={newRegionName}
                    onChange={(e) => setNewRegionName(e.target.value)}
                />
                <button className="btn btn-primary ms-2" onClick={handleCreateRegion}>
                    + Add
                </button>
            </div>

            {/* If no region selected, show a prompt */}
            {!selectedRegion && (
                <div className="alert alert-info">Select a region above or create a new one.</div>
            )}

            {/* Region Form */}
            {selectedRegion && regionData && (
                <>
                    <div className="mb-3">
                        <label>Min Version (Force Update)</label>
                        <input
                            className="form-control"
                            value={regionData.last_force_update || ''}
                            onChange={(e) =>
                                setRegionData({ ...regionData, last_force_update: e.target.value })
                            }
                        />
                    </div>
                    <div className="mb-3">
                        <label>Optional Version</label>
                        <input
                            className="form-control"
                            value={regionData.last_optional_update || ''}
                            onChange={(e) =>
                                setRegionData({ ...regionData, last_optional_update: e.target.value })
                            }
                        />
                    </div>
                    <div className="mb-3">
                        <label>Last Update Description</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={regionData.newest_update_description || ''}
                            onChange={(e) =>
                                setRegionData({ ...regionData, newest_update_description: e.target.value })
                            }
                        />
                    </div>
                    <div className="mb-3">
                        <label>Update Link</label>
                        <input
                            className="form-control"
                            value={regionData.update_link || ''}
                            onChange={(e) => setRegionData({ ...regionData, update_link: e.target.value })}
                        />
                    </div>
                    <div className="mb-3 form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={regionData.is_under_maintenance || false}
                            onChange={(e) =>
                                setRegionData({ ...regionData, is_under_maintenance: e.target.checked })
                            }
                        />
                        <label className="form-check-label">Is Under Maintenance?</label>
                    </div>
                    <div className="mb-3">
                        <label>Maintenance Reason</label>
                        <input
                            className="form-control"
                            value={regionData.maintenance_description || ''}
                            onChange={(e) =>
                                setRegionData({ ...regionData, maintenance_description: e.target.value })
                            }
                        />
                    </div>

                    {/* Servers */}
                    <h3>Servers</h3>
                    {(regionData.servers || []).map((server, index) => (
                        <div key={index} className="card mb-2 p-2">
                            <div className="mb-2">
                                <label>Name</label>
                                <input
                                    className="form-control"
                                    value={server.name}
                                    onChange={(e) => {
                                        const updatedServers = [...regionData.servers];
                                        updatedServers[index].name = e.target.value;
                                        setRegionData({ ...regionData, servers: updatedServers });
                                    }}
                                />
                            </div>
                            <div className="mb-2">
                                <label>Host</label>
                                <input
                                    className="form-control"
                                    value={server.host}
                                    onChange={(e) => {
                                        const updatedServers = [...regionData.servers];
                                        updatedServers[index].host = e.target.value;
                                        setRegionData({ ...regionData, servers: updatedServers });
                                    }}
                                />
                            </div>
                            <div className="mb-2">
                                <label>Scheme</label>
                                <input
                                    className="form-control"
                                    value={server.scheme}
                                    onChange={(e) => {
                                        const updatedServers = [...regionData.servers];
                                        updatedServers[index].scheme = e.target.value;
                                        setRegionData({ ...regionData, servers: updatedServers });
                                    }}
                                />
                            </div>
                            <div className="mb-2">
                                <label>Port</label>
                                <input
                                    className="form-control"
                                    value={server.port}
                                    onChange={(e) => {
                                        const updatedServers = [...regionData.servers];
                                        updatedServers[index].port = e.target.value;
                                        setRegionData({ ...regionData, servers: updatedServers });
                                    }}
                                />
                            </div>
                            <button
                                className="btn btn-sm btn-danger"
                                onClick={() => {
                                    if (window.confirm('Remove this server?')) {
                                        handleRemoveServer(index);
                                    }
                                }}
                            >
                                Remove Server
                            </button>
                        </div>
                    ))}

                    <button className="btn btn-success mb-3" onClick={handleAddServer}>
                        Add Server
                    </button>
                    <br />

                    <button className="btn btn-primary me-2" onClick={handleSaveRegion}>
                        Save Region
                    </button>
                </>
            )}

            <button
                className="btn btn-secondary mt-3"
                onClick={() => navigate(`/admin/packages/${packageName}`)}
            >
                Back
            </button>
        </div>
    );
};

export default MarketDetailsPage;
